import React, { createContext, useContext, useReducer, useEffect } from 'react'
import { videoService } from '../services/videoService'
import { uploadRouter } from '../services/UploadRouter'
import toast from 'react-hot-toast'

const VideoContext = createContext()

const videoReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload
      }
    case 'SET_VIDEOS':
      return {
        ...state,
        videos: action.payload,
        loading: false
      }
    case 'ADD_VIDEO':
      return {
        ...state,
        videos: [action.payload, ...state.videos]
      }
    case 'UPDATE_VIDEO':
      return {
        ...state,
        videos: state.videos.map(video =>
          video.id === action.payload.id ? action.payload : video
        )
      }
    case 'DELETE_VIDEO':
      return {
        ...state,
        videos: state.videos.filter(video => video.id !== action.payload)
      }
    case 'SET_UPLOAD_PROGRESS':
      return {
        ...state,
        uploadProgress: action.payload
      }
    case 'SET_UPLOAD_XHR':
      console.log('📡 Reducer: Setting uploadXhr:', action.payload ? 'XHR object' : 'null')
      return {
        ...state,
        uploadXhr: action.payload
      }
    case 'SET_MULTIPART_UPLOADER':
      console.log('📡 Reducer: Setting multipartUploader:', action.payload ? 'Uploader object' : 'null')
      return {
        ...state,
        multipartUploader: action.payload
      }
    case 'SET_SELECTED_VIDEO':
      return {
        ...state,
        selectedVideo: action.payload
      }
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        loading: false
      }
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      }
    default:
      return state
  }
}

const initialState = {
  videos: [],
  selectedVideo: null,
  loading: false,
  uploadProgress: 0,
  error: null,
  uploadXhr: null,
  multipartUploader: null
}

export const VideoProvider = ({ children }) => {
  const [state, dispatch] = useReducer(videoReducer, initialState)
  // Load videos when component mounts and user is authenticated
  useEffect(() => {
    const token = localStorage.getItem('token')
    if (token) {
      loadVideos()
    }
  }, [])

  const loadVideos = async () => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'CLEAR_ERROR' })
    
    try {
      const videos = await videoService.getVideos()
      dispatch({ type: 'SET_VIDEOS', payload: videos })
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to load videos'
      dispatch({ type: 'SET_ERROR', payload: errorMessage })
      toast.error(errorMessage)
    }
  }

  const uploadVideo = async (file, metadata) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_UPLOAD_PROGRESS', payload: 0 })
    dispatch({ type: 'CLEAR_ERROR' })

    try {
      // 🚀 ENTERPRISE: Use unified upload router (replaces all legacy upload methods)
      const result = await uploadRouter.upload(file, metadata, (progress) => {
        dispatch({ type: 'SET_UPLOAD_PROGRESS', payload: progress })
      })

      // Handle cancellation
      if (result.cancelled) {
        dispatch({ type: 'SET_LOADING', payload: false })
        dispatch({ type: 'SET_UPLOAD_PROGRESS', payload: 0 })
        return result
      }

      // Success - add video to list and reset state
      if (result.video) {
        dispatch({ type: 'ADD_VIDEO', payload: result.video })
      }

      dispatch({ type: 'SET_LOADING', payload: false })
      dispatch({ type: 'SET_UPLOAD_PROGRESS', payload: 0 })

      await loadVideos() // Refresh video list
      toast.success('Video uploaded successfully!')

      return result

    } catch (error) {
      console.error('❌ Upload failed:', error)

      // Handle cancellation gracefully
      if (error.message === 'Upload cancelled') {
        dispatch({ type: 'SET_LOADING', payload: false })
        dispatch({ type: 'SET_UPLOAD_PROGRESS', payload: 0 })
        return { cancelled: true }
      }

      // Handle other errors
      const errorMessage = error.response?.data?.message || error.message || 'Upload failed'
      dispatch({ type: 'SET_ERROR', payload: errorMessage })
      dispatch({ type: 'SET_LOADING', payload: false })
      dispatch({ type: 'SET_UPLOAD_PROGRESS', payload: 0 })
      toast.error(errorMessage)
      throw error
    }
  }

  // 🗑️ ENTERPRISE: Legacy upload methods removed - now handled by UploadRouter


  const updateVideoMetadata = async (videoId, metadata) => {
    try {
      const updatedVideo = await videoService.updateVideo(videoId, metadata)
      dispatch({ type: 'UPDATE_VIDEO', payload: updatedVideo })
      toast.success('Video updated successfully!')
      return { success: true }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Update failed'
      toast.error(errorMessage)
      return { error: errorMessage }
    }
  }

  const deleteVideo = async (videoId) => {
    try {
      await videoService.deleteVideo(videoId)
      dispatch({ type: 'DELETE_VIDEO', payload: videoId })
      toast.success('Video deleted successfully!')
      return { success: true }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Delete failed'
      toast.error(errorMessage)
      return { error: errorMessage }
    }
  }

  const getVideoStreamToken = async (videoId) => {
    try {
      const tokenData = await videoService.getStreamToken(videoId)
      return tokenData
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to get video access'
      toast.error(errorMessage)
      throw error
    }
  }

  const selectVideo = async (video) => {
    // Set selected video directly (R2 videos don't need tokens)
    dispatch({
      type: 'SET_SELECTED_VIDEO',
      payload: video
    })
  }

  const clearSelectedVideo = () => {
    dispatch({ type: 'SET_SELECTED_VIDEO', payload: null })
  }

  const searchVideos = (query) => {
    if (!query.trim()) {
      return state.videos
    }
    
    const searchTerm = query.toLowerCase()
    return state.videos.filter(video =>
      video.title.toLowerCase().includes(searchTerm) ||
      video.description.toLowerCase().includes(searchTerm) ||
      video.tags?.some(tag => tag.toLowerCase().includes(searchTerm))
    )
  }

  const getVideosByCategory = (category) => {
    if (!category) {
      return state.videos
    }
    
    return state.videos.filter(video =>
      video.category === category ||
      video.tags?.includes(category)
    )
  }

  const cancelUpload = () => {
    // 🚀 ENTERPRISE: Use unified upload router cancel (replaces all legacy cancel methods)
    console.log('🚫 VideoContext: Delegating to UploadRouter.cancel()')
    uploadRouter.cancel()

    // Reset context state
    dispatch({ type: 'SET_UPLOAD_PROGRESS', payload: 0 })
    dispatch({ type: 'SET_LOADING', payload: false })
    dispatch({ type: 'SET_UPLOAD_XHR', payload: null })
    dispatch({ type: 'SET_MULTIPART_UPLOADER', payload: null })
  }

  const contextValue = {
    ...state,
    loadVideos,
    uploadVideo,
    cancelUpload,
    updateVideoMetadata,
    deleteVideo,
    getVideoStreamToken,
    selectVideo,
    clearSelectedVideo,
    searchVideos,
    getVideosByCategory
  }

  return (
    <VideoContext.Provider value={contextValue}>
      {children}
    </VideoContext.Provider>
  )
}

export const useVideo = () => {
  const context = useContext(VideoContext)
  if (!context) {
    throw new Error('useVideo must be used within a VideoProvider')
  }
  return context
}